import { PageType } from "../config/page.types";
import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { FeatureKey, HttpMethod } from "./apiPermissions";

/**
 * API call (route and HTTP method) registered in ApiPermissions.
 */
export type ApiCall = {
	route: ApiRoutes;
	method: HttpMethod;
};

/**
 * PageRequirement is an API call made by a page or, for calls not defined in ApiRoutes,
 * the backend feature they require.
 */
export type PageRequirement = ApiCall | { feature: FeatureKey };

const RootCauseRequirements: PageRequirement[] = [
	{ route: ApiRoutes.RootCauseWidjetAggregate, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetWaterfall, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetPerimeter, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetTransportQuantity, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetModalShift, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeTotal, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeRoad, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeTrain, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeShip, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeAir, method: "POST" },
	{ route: ApiRoutes.RootCauseWidjetEmissionsChangeWaterways, method: "POST" },
];

/**
 * PagePermissions maps each page to the API calls that load its content; the required features are
 * resolved through ApiPermissions. Only content decides whether a page is accessible: actions (downloads,
 * saves, deletions) are not listed here and are checked where they are triggered (see useWriteResource).
 */
export const PagePermissions: Record<PageType, PageRequirement[]> = {
	// Results
	[PageType.ResultsOverview]: [
		{ route: ApiRoutes.ResultsEmissions, method: "POST" },
		{ route: ApiRoutes.ResultsEmissionsIntervals, method: "POST" },
		{ route: ApiRoutes.ResultsScopeEmissions, method: "POST" },
		{ route: ApiRoutes.ResultsActivityEmissions, method: "POST" },
	],
	[PageType.ResultsTransport]: [
		{ route: ApiRoutes.ResultsTransportEmission, method: "POST" },
		{ route: ApiRoutes.ResultsEmissionType, method: "POST" },
		{ route: ApiRoutes.ResultsChainTransportEmission, method: "POST" },
		{ route: ApiRoutes.ResultsEmissionsIntervals, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyEnvironmentalKpi, method: "POST" },
		{ route: ApiRoutes.ResultsCountryEmission, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyCalculationMethods, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyNodesCertification, method: "POST" },
	],
	[PageType.ResultsTransportGreenRouter]: [{ route: ApiRoutes.ResultsTransportGreenrouter, method: "POST" }],
	[PageType.ResultsTransportGreenRouterEntities]: [{ route: ApiRoutes.ResultsEntitiesGreenrouter, method: "POST" }],
	// Detail pages: accessible only if their main content (the detail) can be loaded.
	[PageType.ResultsTransportGreenRouterDetail]: [{ route: ApiRoutes.ResultsTripDetails, method: "GET" }],
	[PageType.ResultsTransportGreenRouterEntityDetail]: [{ route: ApiRoutes.ResultsEntityDetails, method: "GET" }],
	[PageType.ResultsTransportThirdParty]: [{ route: ApiRoutes.ResultsTripsThirdParty, method: "POST" }],
	[PageType.ResultsTransportThirdPartyDetail]: [{ route: ApiRoutes.ResultsTransportThirdPartyDetail, method: "GET" }],

	// Results > Operational units
	[PageType.ResultsOperationalUnits]: [
		{ route: ApiRoutes.ResultsSitesEmissions, method: "POST" },
		{ route: ApiRoutes.ResultsEmissionsSitesSource, method: "POST" },
		{ route: ApiRoutes.ResultsEmissionsIntervals, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyEnvironmentalKpi, method: "POST" },
		{ route: ApiRoutes.ResultsCountryEmission, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyCalculationMethods, method: "POST" },
		{ route: ApiRoutes.ResultThirdPartyNodesCertification, method: "POST" },
		{ route: ApiRoutes.ResultGreerouterNodesMap, method: "POST" },
		{ route: ApiRoutes.ResultsConsumptionIntervals, method: "POST" },
		{ route: ApiRoutes.ResultsElectricityConsumption, method: "POST" },
		{ route: ApiRoutes.ResultsWaterConsumption, method: "POST" },
		{ route: ApiRoutes.ResultsFuelConsumption, method: "POST" },
		{ route: ApiRoutes.ResultsRefrigerantsConsumption, method: "POST" },
		{ route: ApiRoutes.ResultsHeatSteamConsumption, method: "POST" },
		{ route: ApiRoutes.ResultsProcessGasConsumption, method: "POST" },
	],
	// List page: accessible only if its main content (the list) can be loaded.
	[PageType.ResultsOperationalUnitsGreenRouter]: [{ route: ApiRoutes.ResultGreerouterSitesList, method: "POST" }],
	[PageType.ResultsOperationalUnitsGreenRouterDetail]: [{ route: ApiRoutes.ResultsSitesNodeDetails, method: "GET" }],
	[PageType.ResultsOperationalUnitsThirdParty]: [{ route: ApiRoutes.ResultsSitesThirdParty, method: "POST" }],
	[PageType.ResultsOperationalUnitsThirdPartyDetail]: [
		{ route: ApiRoutes.ResultsSitesThirdPartyDetail, method: "GET" },
	],

	// Data insights
	[PageType.DataInsightsDashboard]: [
		{ route: ApiRoutes.SupersetDashboards, method: "GET" },
		{ feature: "DashboardsSignInSuperset" },
	],
	[PageType.DataInsightsDashboardDetail]: [{ feature: "DashboardsSignInSuperset" }],
	[PageType.DataInsightsHotspots]: [],
	[PageType.DataInsightsRootCause]: RootCauseRequirements,
	[PageType.DataInsightsRootCauseResults]: RootCauseRequirements,

	// What if
	[PageType.WhatIfSimulations]: [],
	[PageType.WhatIfBudgeting]: [],
	[PageType.WhatIfCarbonSnap]: [
		{ route: ApiRoutes.UtilsOptions, method: "GET" },
		{ route: ApiRoutes.CarbonSnapNodesSearch, method: "GET" },
		{ route: ApiRoutes.CarbonSnapGeocode, method: "POST" },
	],
	[PageType.WhatIfCarbonSnapResults]: [{ route: ApiRoutes.CarbonSnapCalculate, method: "POST" }],

	// Data I/O
	[PageType.DataIOMonitor]: [],
	[PageType.DataIOImport]: [],
	[PageType.DataIOAutomatedProcesses]: [],
	[PageType.DataIODataExport]: [],

	// Master data
	[PageType.MasterDataOperationalUnits]: [
		{ route: ApiRoutes.ResultGreerouterSitesList, method: "POST" },
		{ route: ApiRoutes.ResultGreerouterNodesMap, method: "POST" },
	],
	// Creation form: accessible only if the unit can be created.
	[PageType.MasterDataOperationalUnitsAddUnit]: [{ route: ApiRoutes.ConfigurationSitesNodes, method: "POST" }],
	// Detail page: accessible only if its main content (the detail) can be loaded.
	[PageType.MasterDataOperationalUnitsDetail]: [{ route: ApiRoutes.ResultsSitesNodeDetails, method: "GET" }],
	[PageType.MasterDataAssumptions]: [],
	[PageType.MasterDataFleetsVehicles]: [],
	[PageType.MasterDataNetworksRoutes]: [],
	[PageType.MasterDataConversionTables]: [],

	// Know how
	[PageType.KnowHowUserManuals]: [],
	[PageType.KnowHowKnowledgeBase]: [],
	[PageType.KnowHowApiDocumentation]: [],

	// Platform settings
	[PageType.PlatformSettingsDataDomains]: [],
	[PageType.PlatformSettingsAdmin]: [
		{ route: ApiRoutes.UtilsOptions, method: "GET" },
		{ route: ApiRoutes.AdminCompanies, method: "GET" },
		{ route: ApiRoutes.AdminPermissions, method: "GET" },
		{ route: ApiRoutes.AdminRoles, method: "GET" },
		{ route: ApiRoutes.AdminUsers, method: "GET" },
		{ route: ApiRoutes.AdminUserFilters, method: "GET" },
	],
	[PageType.PlatformSettingsApi]: [],

	[PageType.Account]: [{ route: ApiRoutes.AuthMe, method: "GET" }],
};
